// src/rms/components/RequirePin.jsx
// ============================================================================
// A name, then a PIN. Nobody works without both.
// ============================================================================
// Since bookings, payments, assets and blocks became signed writes, a member
// with no PIN cannot do the job. Not "is inconvenienced" -- cannot: the
// database refuses the insert without a signature, and a signature needs a PIN.
//
// Before this, an owner discovered that at the counter, with a customer in
// front of them, on their first real booking.
//
// ----------------------------------------------------------------------------
// The name comes first, and it is not a nicety
// ----------------------------------------------------------------------------
// rms_seed_owner_member writes the owner's row with an email and no
// display_name, so rms_first_name falls back to the local part of the address.
// Every signature, every "Created by" on the availability list and every
// "Opened by" banner then reads `steven.lazaroff` instead of `Steven` -- a
// machine's answer to a question a person asked.
//
// A signature that does not name a person is barely a signature, so the name is
// asked for in the same breath as the PIN and before it. Owners provisioned
// before today are backfilled from their address in 20260821150000; this is for
// anybody the guess does not fit and for everybody who arrives from now on.
//
// ----------------------------------------------------------------------------
// Why here and not in the provisioning flow
// ----------------------------------------------------------------------------
// The vertical picker would only catch businesses created after this shipped.
// This catches those, plus every operator provisioned before signatures
// existed, plus every staff member whose account was made for them, plus
// anybody whose PIN was cleared by an owner. One gate, at the door all of them
// come through.
// ============================================================================

import React, { useCallback, useEffect, useState } from "react";
import { useRms } from "../context/RmsContext";
import pinService from "../data/pinService";
import memberService from "../data/memberService";
import PinModal from "./PinModal";
import { Modal, Field, TextInput, Banner } from "./ui";

export default function RequirePin({ children }) {
  const { operator, member, memberLoading, t, invalidate } = useRms();

  // null = not yet known. Distinct from false, because "we have not asked" and
  // "they do not have one" must not look the same for even one render -- the
  // second puts an undismissable modal on screen.
  const [hasPin, setHasPin] = useState(null);

  const [name, setName] = useState("");
  const [named, setNamed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  const check = useCallback(() => {
    if (!operator?.id || !member?.id) return;
    pinService.hasPin(operator, member).then((r) => setHasPin(!!r.data));
  }, [operator, member]);

  useEffect(() => {
    check();
  }, [check]);

  // Wait for the member before deciding. Asking while their row is still in
  // flight means asking somebody who already has one.
  if (memberLoading || hasPin === null) return children;

  // No member row at all is a different problem, and not one a PIN prompt
  // fixes. Let them through rather than trapping them behind a box that cannot
  // succeed.
  if (!member?.id) return children;

  const needsName = !named && !String(member.display_name || "").trim();

  const saveName = async () => {
    setBusy(true);
    setProblem(null);
    const result = await memberService.setMyDisplayName(operator, name);
    setBusy(false);
    if (!result.success) {
      setProblem(result.error);
      return;
    }
    setNamed(true);
    // So the roster, the banners and anything already on screen catch up
    // without a reload.
    invalidate();
  };

  if (needsName) {
    return (
      <>
        {children}
        {/* No onClose. Same reasoning as the PIN below: there is nothing behind
            this worth more than being able to work, and every signature made
            before it is answered would name an email address. */}
        <Modal
          title={t("name.required", "What should the records call you?")}
          width={380}
          footer={
            <button
              type="button"
              className="rms-btn rms-btn--primary"
              onClick={saveName}
              disabled={busy || !name.trim()}
            >
              {busy ? t("common.working", "Working…") : t("name.save", "Continue")}
            </button>
          }
        >
          {problem ? <Banner tone="danger">{problem}</Banner> : null}

          <p style={{ fontSize: 13.5, color: "var(--rms-muted-strong)", marginTop: 0 }}>
            {t(
              "name.requiredHelp",
              "Your first and last name. It goes beside everything you sign — a booking, a payment, a vehicle handed over — so the record names a person rather than an email address."
            )}
          </p>

          <Field label={t("name.label", "Your name")} required>
            <TextInput
              value={name}
              onChange={setName}
              autoComplete="name"
              placeholder={t("name.placeholder", "Steven Lazaroff")}
            />
          </Field>
        </Modal>
      </>
    );
  }

  if (hasPin) return children;

  return (
    <>
      {children}
      <PinModal
        forceSetup
        title={t("pin.required", "Set your PIN to get started")}
        setupExplain={t(
          "pin.requiredHelp",
          "Four digits, before anything else. Every booking, payment and handover is signed with it — so until you have one, nothing can be taken."
        )}
        // No cancel. Dismissing this leaves somebody signed in and unable to
        // take a booking, which is a worse place to be than this box.
        onCancel={null}
        onResolved={async () => {
          setHasPin(true);
          return {};
        }}
      />
    </>
  );
}
